// Annotation Portal React layouts
import Dashboard from "layouts/dashboard";
import SignIn from "layouts/authentication/sign-in";

// Super Admin
import SuperAdminMain from "layouts/super_admin/admin";
import SuperAdminSamples from "layouts/super_admin/samples";
import SuperAdminGuidelines from "layouts/super_admin/guidelines";

// Admin
import AdminMain from "layouts/admin/annotator";
import AdminSamples from "layouts/admin/samples";
import AdminExams from "layouts/admin/exams";
import AdminQa from "layouts/admin/qa";
import AdminRecordings from "layouts/admin/recordings";
import AdminStudents from "layouts/admin/students";
import AdminCrls from "layouts/admin/crls";
import AdminGuidelines from "layouts/admin/guidelines";
import ViewSamplePage from "layouts/admin/exams/ViewSamplePage";

// Annotator
import AnnotatorSamples from "layouts/annotator/samples";
import AnnotatorExams from "layouts/annotator/exams";
import SampleDetails from "layouts/annotator/samples/modals/SampleDetails";
import AnnotatorGuidelines from "layouts/annotator/guidelines";

import AnnotationPage from "layouts/annotator/exams/annotation";

// @mui icons
import Icon from "@mui/material/Icon";

// Your custom translation hook
import { useTranslation } from "hooks/useTranslation";

// Custom hook to get translated routes
const useTranslatedRoutes = () => {
  const { t } = useTranslation();

  const routes = [
    // Dashboard (Accessible to all roles)
    {
      type: "collapse",
      name: t("navigation.dashboard"),
      key: "dashboard",
      icon: <Icon fontSize="small">dashboard</Icon>,
      route: "/dashboard",
      component: <Dashboard />,
      roles: ["SuperAdmin", "Admin", "Annotator"],
    },
    // Super Admin Routes
    {
      type: "collapse",
      name: t("navigation.users"),
      key: "SuperAdmin_main",
      icon: <Icon fontSize="small">admin_panel_settings</Icon>,
      route: "/superAdmin/admin",
      component: <SuperAdminMain />,
      roles: ["SuperAdmin"],
    },
    // Admin Routes
    {
      type: "collapse",
      name: t("navigation.annotator"),
      key: "Admin_main",
      icon: <Icon fontSize="small">people</Icon>,
      route: "/admin/annotator",
      component: <AdminMain />,
      roles: ["Admin"],
    },
    {
      type: "collapse",
      name: t("navigation.samples"),
      key: "Admin_samples",
      icon: <Icon fontSize="small">fact_check</Icon>,
      route: "/admin/exams",
      component: <AdminExams />,
      roles: ["SuperAdmin", "Admin"],
    },
    {
      type: "collapse",
      name: t("navigation.qa"),
      key: "Admin_qa",
      icon: <Icon fontSize="small">verified</Icon>,
      route: "/admin/exams/qa",
      component: <AdminQa />,
      roles: ["SuperAdmin", "Admin"],
    },
    {
      type: "collapse",
      name: t("navigation.samples"),
      key: "Annotator_exams",
      icon: <Icon fontSize="small">fact_check</Icon>,
      route: "/annotator/exams",
      component: <AnnotatorExams />,
      roles: ["Annotator"],
    },
    {
      type: "collapse",
      name: t("navigation.students"),
      key: "Student_samples",
      icon: <Icon fontSize="small">people</Icon>,
      route: "/admin/students",
      component: <AdminStudents />,
      roles: ["SuperAdmin", "Admin"],
    },
    {
      type: "collapse",
      name: t("navigation.crls"),
      key: "Admin_crls",
      icon: <Icon fontSize="small">badge</Icon>,
      route: "/admin/crls",
      component: <AdminCrls />,
      roles: ["SuperAdmin", "Admin"],
    },
    {
      type: "collapse",
      name: t("navigation.guidelines"),
      key: "Admin_guidelines",
      icon: <Icon fontSize="small">rule</Icon>,
      route: "/admin/guidelines",
      component: <AdminGuidelines />,
      roles: ["SuperAdmin", "Admin"],
    },

    // Annotator Routes
    {
      type: "collapse",
      name: t("navigation.guidelines"),
      key: "Annotator_guidelines",
      icon: <Icon fontSize="small">rule</Icon>,
      route: "/annotator/guidelines",
      component: <AnnotatorGuidelines />,
      roles: ["Annotator"],
    },

    // Authentication
    /*   {
      type: "collapse",
      name: t("navigation.signIn"),
      key: "sign_in",
      icon: <Icon fontSize="small">login</Icon>,
      route: "/authentication/sign-in",
      component: <SignIn />,
      roles: ["SuperAdmin", "Admin", "Annotator"],
    } */
    // Hidden route (no sidebar item)
    {
      type: "none",
      name: t("navigation.annotation"),
      key: "annotator-exams-annotation",
      route: "/annotator/exams/annotation",
      component: <AnnotationPage />,
      noCollapse: true,
      roles: ["Annotator"],
    },
    // Hidden route — admin view sample page
    {
      type: "none",
      name: "View Sample",
      key: "admin-exams-view-sample",
      route: "/admin/exams/view-sample",
      component: <ViewSamplePage />,
      noCollapse: true,
      roles: ["SuperAdmin", "Admin"],
    },
  ];

  return routes;
};

// Export the hook for component usage
export { useTranslatedRoutes };

// Export default routes for backward compatibility (without translation)
const defaultRoutes = [
  // Dashboard (Accessible to all roles)
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
    roles: ["SuperAdmin", "Admin", "Annotator"],
  },
  // Super Admin Routes
  {
    type: "collapse",
    name: "Users",
    key: "SuperAdmin_main",
    icon: <Icon fontSize="small">admin_panel_settings</Icon>,
    route: "/superAdmin/admin",
    component: <SuperAdminMain />,
    roles: ["SuperAdmin"],
  },
  // Admin Routes
  {
    type: "collapse",
    name: "Annotator",
    key: "Admin_main",
    icon: <Icon fontSize="small">people</Icon>,
    route: "/admin/annotator",
    component: <AdminMain />,
    roles: ["Admin"],
  },
  {
    type: "collapse",
    name: "Samples",
    key: "Admin_samples",
    icon: <Icon fontSize="small">fact_check</Icon>,
    route: "/admin/exams",
    component: <AdminExams />,
    roles: ["SuperAdmin", "Admin"],
  },
  {
    type: "collapse",
    name: "Samples",
    key: "Annotator_exams",
    icon: <Icon fontSize="small">fact_check</Icon>,
    route: "/annotator/exams",
    component: <AnnotatorExams />,
    roles: ["Annotator"],
  },
  {
    type: "collapse",
    name: "Data Collectors",
    key: "Admin_crls",
    icon: <Icon fontSize="small">badge</Icon>,
    route: "/admin/crls",
    component: <AdminCrls />,
    roles: ["SuperAdmin", "Admin"],
  },
  {
    type: "collapse",
    name: "Guidelines",
    key: "Admin_guidelines",
    icon: <Icon fontSize="small">rule</Icon>,
    route: "/admin/guidelines",
    component: <AdminGuidelines />,
    roles: ["SuperAdmin", "Admin"],
  },

  // Annotator Routes
  {
    type: "collapse",
    name: "Guidelines",
    key: "Annotator_guidelines",
    icon: <Icon fontSize="small">rule</Icon>,
    route: "/annotator/guidelines",
    component: <AnnotatorGuidelines />,
    roles: ["Annotator"],
  },

  // Hidden route (no sidebar item)
  {
    type: "none",
    name: "Annotation",
    key: "annotator-exams-annotation",
    route: "/annotator/exams/annotation",
    component: <AnnotationPage />,
    noCollapse: true,
    roles: ["Annotator"],
  },
  // Hidden route — admin view sample page
  {
    type: "none",
    name: "View Sample",
    key: "admin-exams-view-sample",
    route: "/admin/exams/view-sample",
    component: <ViewSamplePage />,
    noCollapse: true,
    roles: ["SuperAdmin", "Admin"],
  },
];

export default defaultRoutes;
